// In-memory mock database so the portal is fully usable standalone (no backend
// required) when REACT_APP_USE_MOCKS=true. This is dev/demo scaffolding only —
// the real data path is src/store/baseQuery.ts → the Smart Links API.
//
// RULE-01: this mock has no notion of customer_id in inputs; it behaves as a
// single authenticated tenant, exactly as the real backend would after
// resolving x-api-key. RULE-02: seed data uses contact_ref, never raw PII.

import type {
  AnalyticsEvent,
  Channel,
  CreateLinkInput,
  Link,
  LinkAnalytics,
  ListLinksParams,
  ListLinksResult,
  Platform,
  Purpose,
  TimeseriesPoint,
} from '../types/link';
import { SHORT_DOMAIN, generateSlug } from '../lib/slug';

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86_400_000).toISOString();
}

function daysFromNow(n: number): string {
  return new Date(Date.now() + n * 86_400_000).toISOString();
}

interface Seed {
  linkName: string;
  purpose: Purpose;
  destination: string;
  channel: Channel;
  campaignId: string;
  campaignName: string;
  clicks: number;
  installRate: number;
  ageDays: number;
  expiresInDays?: number | null;
  hasApp?: boolean;
}

const SEEDS: Seed[] = [
  { linkName: 'Eid sale · WA blast',     purpose: 'app_download', destination: 'https://shop.example.com/eid-sale',      channel: 'WHATSAPP', campaignId: '1042', campaignName: 'Eid promo · app push',     clicks: 18430, installRate: 0.21, ageDays: 12, expiresInDays: 20, hasApp: true },
  { linkName: 'Premium upgrade SMS',     purpose: 'deep_link',    destination: 'https://bank.example.com/app/upgrade',    channel: 'SMS',      campaignId: '1043', campaignName: 'Premium upgrade · DL',     clicks: 9120,  installRate: 0.34, ageDays: 6,  hasApp: true },
  { linkName: 'Breaking news push',      purpose: 'website',      destination: 'https://news.example.com/breaking',       channel: 'PUSH',     campaignId: '1044', campaignName: 'Breaking news',            clicks: 5210,  installRate: 0,    ageDays: 3 },
  { linkName: 'Summer travel · email',   purpose: 'website',      destination: 'https://travel.example.com/summer',       channel: 'EMAIL',    campaignId: '1045', campaignName: 'Summer sale',              clicks: 2740,  installRate: 0.12, ageDays: 30, expiresInDays: -2 },
  { linkName: 'Fitness app launch · WA', purpose: 'app_download', destination: 'https://fitness.example.com/get-app',     channel: 'WHATSAPP', campaignId: '1046', campaignName: 'Fitness app launch',       clicks: 14005, installRate: 0.28, ageDays: 1,  hasApp: true },
  { linkName: 'Re-order nudge SMS',      purpose: 'deep_link',    destination: 'https://food.example.com/order',          channel: 'SMS',      campaignId: '1047', campaignName: 'Re-order nudge',           clicks: 760,   installRate: 0.09, ageDays: 45, hasApp: true },
  { linkName: 'Wallet topup IVR',        purpose: 'website',      destination: 'https://wallet.example.com/topup',        channel: 'VOICE',    campaignId: '1048', campaignName: 'Wallet topup',             clicks: 320,   installRate: 0,    ageDays: 9 },
];

function buildLink(seed: Seed): Link {
  const installs = Math.round(seed.clicks * seed.installRate);
  const opens = Math.round(seed.clicks * 0.06);
  const expiresAt =
    seed.expiresInDays === null || seed.expiresInDays === undefined
      ? null
      : daysFromNow(seed.expiresInDays);
  const expired = expiresAt != null && new Date(expiresAt).getTime() < Date.now();
  const code = generateSlug();
  return {
    id: uuid(),
    linkName: seed.linkName,
    shortCode: code,
    shortUrl: `https://${SHORT_DOMAIN}/${code}`,
    purpose: seed.purpose,
    destination: seed.destination,
    channel: seed.channel,
    campaignId: seed.campaignId,
    campaignName: seed.campaignName,
    contactRef: 'subscriber_' + generateSlug(8),
    websiteUrl: seed.purpose === 'website' ? seed.destination : undefined,
    iosAppStoreUrl: seed.hasApp ? 'https://apps.apple.com/app/id000000000' : undefined,
    androidAppStoreUrl: seed.hasApp ? 'https://play.google.com/store/apps/details?id=com.example' : undefined,
    ipadAppStoreUrl: seed.hasApp && seed.purpose === 'app_download' ? 'https://apps.apple.com/app/id111111111' : undefined,
    huaweiAppGalleryUrl: seed.hasApp && seed.purpose === 'app_download' ? 'https://appgallery.huawei.com/app/C000000' : undefined,
    webFallbackUrl: seed.destination,
    androidDeepLink: seed.purpose === 'deep_link' ? 'exampleapp://product/123' : undefined,
    iosDeepLink: seed.purpose === 'deep_link' ? 'exampleapp://product/123' : undefined,
    appScheme: seed.hasApp ? 'exampleapp' : undefined,
    deepLinkPath: seed.hasApp ? '/product/123' : undefined,
    utmParameters: { utm_source: 'xnotify', utm_medium: seed.channel.toLowerCase() },
    attributionWindowHours: 168,
    status: expired ? 'expired' : 'active',
    isActive: !expired,
    expiresAt,
    createdAt: daysAgo(seed.ageDays),
    updatedAt: daysAgo(Math.max(0, seed.ageDays - 1)),
    metrics: {
      clicks: seed.clicks,
      installs,
      opens,
      installRate: seed.clicks ? installs / seed.clicks : 0,
    },
  };
}

const store: Map<string, Link> = new Map();
SEEDS.forEach((s) => {
  const link = buildLink(s);
  store.set(link.id, link);
});

function recomputeStatus(link: Link): Link {
  const expired = link.expiresAt != null && new Date(link.expiresAt).getTime() < Date.now();
  const status = !link.isActive ? 'disabled' : expired ? 'expired' : 'active';
  return { ...link, status };
}

export function listLinks(params: ListLinksParams): ListLinksResult {
  let items = Array.from(store.values()).map(recomputeStatus);

  if (params.search) {
    const q = params.search.toLowerCase();
    items = items.filter(
      (l) =>
        l.shortCode.toLowerCase().includes(q) ||
        l.destination.toLowerCase().includes(q) ||
        (l.campaignId ?? '').toLowerCase().includes(q) ||
        (l.campaignName ?? '').toLowerCase().includes(q),
    );
  }
  if (params.status && params.status !== 'all') {
    items = items.filter((l) => l.status === params.status);
  }
  if (params.channel && params.channel !== 'all') {
    items = items.filter((l) => l.channel === params.channel);
  }
  if (params.campaignId) {
    items = items.filter((l) => l.campaignId === params.campaignId);
  }

  const sortBy = params.sortBy ?? 'createdAt';
  const dir = params.sortDir === 'asc' ? 1 : -1;
  items.sort((a, b) => {
    let av: number;
    let bv: number;
    if (sortBy === 'clicks') {
      av = a.metrics.clicks;
      bv = b.metrics.clicks;
    } else if (sortBy === 'installs') {
      av = a.metrics.installs;
      bv = b.metrics.installs;
    } else {
      av = new Date(a.createdAt).getTime();
      bv = new Date(b.createdAt).getTime();
    }
    return (av - bv) * dir;
  });

  const total = items.length;
  const start = (params.page - 1) * params.pageSize;
  return {
    items: items.slice(start, start + params.pageSize),
    total,
    page: params.page,
    pageSize: params.pageSize,
  };
}

export function getLink(id: string): Link | undefined {
  const l = store.get(id);
  return l ? recomputeStatus(l) : undefined;
}

export function createLink(input: CreateLinkInput): Link {
  // Honor the client-suggested slug if free, otherwise generate (the real
  // backend does the same — it has the authority on uniqueness).
  let code = input.slug;
  const codeIsTaken = (s: string) => Array.from(store.values()).some((l) => l.shortCode === s);
  if (!code || codeIsTaken(code)) code = generateSlug();
  const now = new Date().toISOString();
  const link: Link = {
    id: uuid(),
    linkName: input.linkName,
    shortCode: code,
    shortUrl: `https://${SHORT_DOMAIN}/${code}`,
    purpose: input.purpose,
    destination: input.destination,
    channel: input.channel,
    campaignId: input.campaignId,
    campaignName: input.campaignName,
    contactRef: input.contactRef,
    websiteUrl: input.websiteUrl,
    iosAppStoreUrl: input.iosAppStoreUrl,
    androidAppStoreUrl: input.androidAppStoreUrl,
    ipadAppStoreUrl: input.ipadAppStoreUrl,
    huaweiAppGalleryUrl: input.huaweiAppGalleryUrl,
    webFallbackUrl: input.webFallbackUrl,
    androidDeepLink: input.androidDeepLink,
    iosDeepLink: input.iosDeepLink,
    appScheme: input.appScheme,
    iosUniversalLink: input.iosUniversalLink,
    androidAppLink: input.androidAppLink,
    deepLinkPath: input.deepLinkPath,
    deepLinkParameters: input.deepLinkParameters,
    utmParameters: input.utmParameters,
    attributionWindowHours: input.attributionWindowHours,
    status: 'active',
    isActive: true,
    expiresAt: input.expiresAt ?? null,
    createdAt: now,
    updatedAt: now,
    metrics: { clicks: 0, installs: 0, opens: 0, installRate: 0 },
  };
  store.set(link.id, link);
  return link;
}

export function expireLink(id: string): Link | undefined {
  const l = store.get(id);
  if (!l) return undefined;
  const updated = recomputeStatus({ ...l, isActive: false, updatedAt: new Date().toISOString() });
  store.set(id, updated);
  return updated;
}

// Deterministic-ish analytics generation per link so charts look real and stable.
function seededRandom(seed: number): () => number {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function hashCode(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function getAnalytics(id: string, windowDays = 30): LinkAnalytics | undefined {
  const link = getLink(id);
  if (!link) return undefined;

  const rng = seededRandom(hashCode(link.id) + windowDays);
  const totalClicks = link.metrics.clicks;
  const totalInstalls = link.metrics.installs;
  const totalOpens = link.metrics.opens;

  const weights: number[] = [];
  let weightSum = 0;
  for (let d = 0; d < windowDays; d += 1) {
    const w = 0.5 + rng();
    weights.push(w);
    weightSum += w;
  }
  const timeseries: TimeseriesPoint[] = weights.map((w, idx) => {
    const dayIso = daysAgo(windowDays - 1 - idx).slice(0, 10);
    const share = w / weightSum;
    return {
      date: dayIso,
      clicks: Math.round(totalClicks * share),
      installs: Math.round(totalInstalls * share),
      opens: Math.round(totalOpens * share),
    };
  });

  const hasApp = link.purpose !== 'website';
  const byPlatform = hasApp
    ? [
        { label: 'Android', value: Math.round(totalClicks * 0.54) },
        { label: 'iOS', value: Math.round(totalClicks * 0.33) },
        { label: 'Web', value: Math.round(totalClicks * 0.13) },
      ]
    : [
        { label: 'Web', value: Math.round(totalClicks * 0.72) },
        { label: 'Android', value: Math.round(totalClicks * 0.18) },
        { label: 'iOS', value: Math.round(totalClicks * 0.1) },
      ];

  const byChannel = [{ label: link.channel, value: totalClicks }];

  const platforms: Platform[] = ['android', 'ios', 'web'];
  const eventNames: AnalyticsEvent['eventName'][] = hasApp
    ? ['link_clicked', 'app_installed', 'app_first_open', 'app_opened']
    : ['link_clicked'];
  const cities = ['Karachi', 'Lahore', 'Islamabad', 'Dubai', 'London'];
  const recentEvents: AnalyticsEvent[] = Array.from({ length: Math.min(8, Math.max(1, Math.ceil(totalClicks / 50))) }).map(
    (_, i) => ({
      id: uuid(),
      eventName: eventNames[Math.floor(rng() * eventNames.length)],
      platform: platforms[Math.floor(rng() * platforms.length)],
      country: 'PK',
      city: cities[Math.floor(rng() * cities.length)],
      at: new Date(Date.now() - i * 3_600_000 - Math.floor(rng() * 3_600_000)).toISOString(),
    }),
  );

  return {
    linkId: link.id,
    shortCode: link.shortCode,
    windowDays,
    totals: {
      clicks: totalClicks,
      installs: totalInstalls,
      opens: totalOpens,
      installRate: link.metrics.installRate,
      ctr: hasApp ? 0.41 : 0,
    },
    timeseries,
    byPlatform,
    byChannel,
    recentEvents,
  };
}
